import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { register, verifyEmail } from '../services/auth';

type RegistrationMethod = 'email' | 'phone';

const Register = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [registrationMethod, setRegistrationMethod] = useState<RegistrationMethod>('email');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    password2: '',
    language: 'en',
    email_notifications: false,
    sms_notifications: false,
    marketing_emails: false,
    marketing_sms: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRegistrationMethodChange = (
    event: React.MouseEvent<HTMLElement>,
    newMethod: RegistrationMethod | null,
  ) => {
    if (newMethod !== null) {
      setRegistrationMethod(newMethod);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.password2) {
      setError(t('passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    try {
      // Use email/phone as username if not provided
      const data = {
        ...formData,
        username: formData.username || (registrationMethod === 'email' ? formData.email : formData.phone),
      };
      await register(data);
      setShowVerification(true);
    } catch (err: any) {
      setError(err.message || t('registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    setVerifying(true);
    setError('');

    try {
      await verifyEmail({
        username: formData.username || (registrationMethod === 'email' ? formData.email : formData.phone),
        verification_code: verificationCode,
      });
      window.location.href = '/login';
    } catch (err: any) {
      setError(err.message || t('verificationFailed'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t('register')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={registrationMethod}
              exclusive
              onChange={handleRegistrationMethodChange}
              aria-label="registration method"
            >
              <ToggleButton value="email" aria-label="email registration">
                {t('emailRegistration')}
              </ToggleButton>
              <ToggleButton value="phone" aria-label="phone registration">
                {t('phoneRegistration')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <form onSubmit={handleSubmit}>
            {registrationMethod === 'email' ? (
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('phone')}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1234567890"
              />
            )}

            <TextField
              margin="normal"
              fullWidth
              label={t('username')}
              name="username"
              value={formData.username}
              onChange={handleChange}
              helperText={t('usernameOptional')}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label={t('password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label={t('confirmPassword')}
              name="password2"
              type="password"
              value={formData.password2}
              onChange={handleChange}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('notificationPreferences')}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  name="email_notifications"
                  checked={formData.email_notifications}
                  onChange={handleChange}
                />
              }
              label={
                <Box>
                  {t('emailNotifications')}
                  <Link href="/consent/email" target="_blank" sx={{ ml: 1 }}>
                    {t('manageConsent')}
                  </Link>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="sms_notifications"
                  checked={formData.sms_notifications}
                  onChange={handleChange}
                />
              }
              label={
                <Box>
                  {t('smsNotifications')}
                  <Link href="/consent/sms" target="_blank" sx={{ ml: 1 }}>
                    {t('manageConsent')}
                  </Link>
                </Box>
              }
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('marketingPreferences')}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  name="marketing_emails"
                  checked={formData.marketing_emails}
                  onChange={handleChange}
                />
              }
              label={
                <Box>
                  {t('marketingEmails')}
                  <Link href="/consent/marketing-email" target="_blank" sx={{ ml: 1 }}>
                    {t('manageConsent')}
                  </Link>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="marketing_sms"
                  checked={formData.marketing_sms}
                  onChange={handleChange}
                />
              }
              label={
                <Box>
                  {t('marketingSms')}
                  <Link href="/consent/marketing-sms" target="_blank" sx={{ ml: 1 }}>
                    {t('manageConsent')}
                  </Link>
                </Box>
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('register')}
            </Button>
          </form>
        </Paper>
      </Box>

      <Dialog open={showVerification} onClose={() => {}}>
        <DialogTitle>
          {registrationMethod === 'email' ? t('verifyEmail') : t('verifyPhone')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {registrationMethod === 'email' 
              ? t('verificationCodeSentEmail')
              : t('verificationCodeSentPhone')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('verificationCode')}
            type="text"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleVerification}
            disabled={verifying || verificationCode.length !== 6}
          >
            {verifying ? <CircularProgress size={24} /> : t('verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Register; 